<template>
  <Teleport to=".app-root">
  <div
    class="filters-panel"
    :class="show
      ? 'fixed inset-0 z-[calc(var(--z-nav)-1)] bg-backdrop'
      : ''"
    @mousedown="$emit('close')"
  >
    <div
      class="fixed top-23 right-0 bottom-0 z-[calc(var(--z-nav)+2)] flex w-80 flex-col overflow-hidden
             border-l border-line bg-chrome [backdrop-filter:var(--glass-blur)] shadow-[var(--shadow-popover)]
             transition-transform duration-150 max-sm:top-(--mobile-header-height)"
      :class="show ? 'translate-x-0' : 'translate-x-80'"
      @mousedown.stop
    >
      <template v-if="opened">
      <div class="flex flex-none items-center gap-3 border-b border-line px-4 py-3.5">
        <div class="mr-auto text-group font-medium text-text">{{ $t('taskFilters.title') }}</div>
        <button
          type="button"
          class="flex h-7 cursor-pointer items-center border-0 bg-transparent px-2.5 py-0
                 text-label text-muted-soft [font-family:inherit]
                 transition-colors duration-150 enabled:hover:text-accent
                 disabled:cursor-default disabled:opacity-45"
          :disabled="!hasActiveFilters"
          @click="clearAll"
        >
          {{ $t('search.clear_all') }}
        </button>
        <button
          type="button"
          class="flex h-7 w-7 cursor-pointer items-center justify-center border-0
                 bg-transparent px-1.5 py-px text-muted-soft rounded-chip
                 transition-colors duration-150 hover:bg-hover hover:text-accent"
          @click="$emit('close')"
        >
          <IconXMark16Solid class="size-4 shrink-0" />
        </button>
      </div>

      <!-- one flex child per section, so the 24px gap sits between sections only -->
      <div
        class="v-tracklify-scroll flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto
               px-4 pt-4 pb-10"
      >
        <div>
          <div class="mb-2 text-body font-medium text-accent">
            {{ $t('taskFilters.executor') }}
          </div>
          <div
            class="flex h-9 cursor-pointer items-center rounded-chip px-1.5 text-text
                   transition-colors duration-150 hover:bg-hover"
            :key="`execut-${i}`"
            v-for="u, i in projectUsersAndUnassignedSliced"
          >
            <Checkbox
              size="sm"
              :modelValue="checkState('executorIds', u && u.id)"
              @update:modelValue="triggerCheck('executorIds', u && u.id)"
            >
              <div class="flex min-w-0 items-center">
                <UserAvatar withDayoffBadge noPendingBadge :user="u" class="size-7! shrink-0" />
                <div class="ml-3 min-w-0 flex-1 truncate text-body">
                  {{ u ? u.username : $t('search.unassigned') }}
                </div>
              </div>
            </Checkbox>
          </div>
          <button
            v-if="projectUsersAndUnassigned.length > MORE_THRESHOLD"
            type="button"
            class="mt-1 ml-1.5 cursor-pointer border-0 bg-transparent px-1.5 py-px
                   text-label uppercase text-accent [font-family:inherit]"
            @click="moreAssignees=!moreAssignees"
          >
            {{ moreAssignees ? $t('taskFilters.hide') : $t('taskFilters.show_more') }}
            {{ moreAssignees ? '' : `(${projectUsersAndUnassigned.length - MORE_THRESHOLD})` }}
          </button>
        </div>

        <div>
          <div class="mb-2 text-body font-medium text-accent">
            {{ $t('taskFilters.withTags') }}
          </div>
          <div
            class="flex h-9 cursor-pointer items-center rounded-chip px-1.5 text-text
                   transition-colors duration-150 hover:bg-hover"
            :key="`tag-${i}`"
            v-for="t, i in projectTagsSliced"
          >
            <Checkbox
              size="sm"
              v-if="!(t && focusedTagId && t.id === focusedTagId)"
              :modelValue="checkState('tagIds', t && t.id)"
              @update:modelValue="triggerCheck('tagIds', t && t.id)"
            >
              <div class="tag-row flex min-w-0 items-center">
                <TagIcon :tag="t" class="h-5! w-8! shrink-0 rounded-sm! p-0! shadow-none!" />
                <div class="ml-3 min-w-0 flex-1 truncate text-body">
                  {{ t ? t.name : $t('search.noTag') }}
                </div>
              </div>
            </Checkbox>
          </div>
          <button
            v-if="projectTags.length > MORE_THRESHOLD"
            type="button"
            class="mt-1 ml-1.5 cursor-pointer border-0 bg-transparent px-1.5 py-px
                   text-label uppercase text-accent [font-family:inherit]"
            @click="moreTags=!moreTags"
          >
            {{ moreTags ? $t('taskFilters.hide') : $t('taskFilters.show_more') }}
            {{moreTags ? '' : `(${projectTags.length - MORE_THRESHOLD})`}}
          </button>
        </div>

        <div>
          <div class="mb-2 text-body font-medium text-accent">
            {{ $t('taskFilters.createdBy') }}
          </div>
          <div
            class="flex h-9 cursor-pointer items-center rounded-chip px-1.5 text-text
                   transition-colors duration-150 hover:bg-hover"
            :key="`creat-${i}`"
            v-for="u, i in projectUsersSliced"
          >
            <Checkbox
              size="sm"
              :modelValue="checkState('createdByIds', u && u.id)"
              @update:modelValue="triggerCheck('createdByIds', u && u.id)"
            >
              <div class="flex min-w-0 items-center">
                <UserAvatar withDayoffBadge noPendingBadge :user="u" class="size-7! shrink-0" />
                <div class="ml-3 min-w-0 flex-1 truncate text-body">{{ u.username }}</div>
              </div>
            </Checkbox>
          </div>
          <button
            v-if="projectUsers.length > MORE_THRESHOLD"
            type="button"
            class="mt-1 ml-1.5 cursor-pointer border-0 bg-transparent px-1.5 py-px
                   text-label uppercase text-accent [font-family:inherit]"
            @click="moreCreators=!moreCreators"
          >
            {{ moreCreators ? $t('taskFilters.hide') : $t('taskFilters.show_more') }}
            {{moreCreators ? '' : `(${projectUsers.length - MORE_THRESHOLD})`}}
          </button>
        </div>

        <div>
          <div class="mb-2 text-body font-medium text-accent">
            {{ $t('taskFilters.createdTime') }}
          </div>
          <div class="light:[&_.field-shell]:bg-input-solid">
            <DatePicker
              :model-value="datesRange"
              :placeholder="$t('reports.select_date_range')"
              :locale="workspace && workspace.locale"
              @update:model-value="
                val => {
                  if (val[0] && val[1]) {
                    setDateInput({ start: val[0], end: val[1] });
                  }
                }
              "
              range
              :model-type="'iso'"
              format="MMM dd, yy"
              validate
              show-clear
              @clear="clearDates"
            >
              <template #menu-header>
                <div class="flex items-center gap-2">
                  <input
                    class="h-8 min-w-0 flex-1 rounded-chip border border-line bg-input px-2.5 py-0
                           font-mono text-label font-medium whitespace-nowrap text-text
                           outline-none transition-[border-color] duration-150
                           focus:border-accent"
                    :value="isoStart"
                    @change="onIsoChange('start', $event.target.value)"
                  />
                  <span class="text-text-muted">—</span>
                  <input
                    class="h-8 min-w-0 flex-1 rounded-chip border border-line bg-input px-2.5 py-0
                           font-mono text-label font-medium whitespace-nowrap text-text
                           outline-none transition-[border-color] duration-150
                           focus:border-accent"
                    :value="isoEnd"
                    @change="onIsoChange('end', $event.target.value)"
                  />
                </div>
              </template>
            </DatePicker>
            <div class="mt-2 grid grid-cols-2 gap-2">
              <SimpleButton
                class="h-7! min-h-7! w-full min-w-0"
                :pressed="activePreset === item.id"
                size="sm"
                v-for="item in presetDatePicker"
                :key="item.id"
                @click="applyPreset(item)"
              >
                {{ $t(item.name) }}
              </SimpleButton>
            </div>
          </div>
        </div>

        <div>
          <div class="mb-2 text-body font-medium text-accent">
            {{ $t('taskFilters.assignee') }}
          </div>
          <div
            class="flex h-9 cursor-pointer items-center rounded-chip px-1.5 text-text
                   transition-colors duration-150 hover:bg-hover"
            :key="`assi-${i}`"
            v-for="u, i in projectUsersAndUnassignedSlicedAssignee"
          >
            <Checkbox
              size="sm"
              :modelValue="checkState('assigneeIds', u && u.id)"
              @update:modelValue="triggerCheck('assigneeIds', u && u.id)"
            >
              <div class="flex min-w-0 items-center">
                <UserAvatar withDayoffBadge noPendingBadge :user="u" class="size-7! shrink-0" />
                <div class="ml-3 min-w-0 flex-1 truncate text-body">
                  {{ u ? u.username : $t('search.unassigned') }}
                </div>
              </div>
            </Checkbox>
          </div>
          <button
            v-if="projectUsersAndUnassigned.length > MORE_THRESHOLD"
            type="button"
            class="mt-1 ml-1.5 cursor-pointer border-0 bg-transparent px-1.5 py-px
                   text-label uppercase text-accent [font-family:inherit]"
            @click="moreAssigneeFilter=!moreAssigneeFilter"
          >
            {{ moreAssigneeFilter ? $t('taskFilters.hide') : $t('taskFilters.show_more') }}
            {{ moreAssigneeFilter ? '' : `(${projectUsersAndUnassigned.length - MORE_THRESHOLD})` }}
          </button>
        </div>
      </div>

      <div class="flex-none border-t border-line p-4">
        <SimpleButton
          variant="accent"
          class="h-10! min-h-10! w-full rounded-chip! text-body!"
          @click="$emit('close')"
        >
          {{ $t('taskFilters.applyFilters') }}
        </SimpleButton>
      </div>
      </template>
    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import TagIcon from '@/components/elements/TagIcon';
import dayjs from 'dayjs';
import DatePicker from '@/components/elements/DatePicker.vue';
import { IconXMark16Solid } from '@iconify-prerendered/vue-heroicons';

const MORE_THRESHOLD = 7;

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const store = useStore();

// the panel content (with the date picker) is created on the first open, not with every board
const opened = ref(props.show);
watch(() => props.show, (show) => {
  if (show) opened.value = true;
});

const filterState = ref({

});
const moreAssignees = ref(false);
const moreAssigneeFilter = ref(false);
const moreTags = ref(false);
const moreCreators = ref(false);
const activePreset = ref(null);

const project = computed(() => store.state.project.project);
const projectTasksFilter = computed(() => store.state.project.projectTasksFilter);
const user = computed(() => store.state.user.user);
const workspace = computed(() => store.state.user.workspace);

// Current project member and focused tag id (Tag Focus mode)
const currentMember = computed(() => {
  if (!project.value?.projectMembers || !user.value) return null;
  return project.value.projectMembers.find(pm => pm.userId === user.value.id) || null;
});
const focusedTagId = computed(() => (currentMember.value && currentMember.value.tagFocus ? currentMember.value.tagFocus : null));
const createdAtRange = computed(() => projectTasksFilter.value.createdAtRange);
// the same array while the dates are the same: a new array on every render (e.g. when the filter
// answer arrives) resets a range the user is halfway through picking
const datesRange = computed((previous) => {
  const range = createdAtRange.value;
  if (!range) return null;
  if (previous && previous[0] === range.start && previous[1] === range.end) return previous;
  return [range.start, range.end];
});
const isoStart = computed(() => (createdAtRange.value ? dayjs(createdAtRange.value.start).format('YYYY-MM-DD') : ''));
const isoEnd = computed(() => (createdAtRange.value ? dayjs(createdAtRange.value.end).format('YYYY-MM-DD') : ''));
// the range can also be cleared outside the panel (chip ×, header Clear)
watch(createdAtRange, (range) => {
  if (!range) activePreset.value = null;
});
const hasActiveFilters = computed(() => {
  const f = projectTasksFilter.value || {};
  return Boolean(
    (f.assigneeIds && f.assigneeIds.length)
    || (f.executorIds && f.executorIds.length)
    || (f.tagIds && f.tagIds.length)
    || (f.createdByIds && f.createdByIds.length)
    || f.createdAtRange
    || f.searchString,
  );
});

const mappedUsers = computed(() => ((project.value && project.value.projectMembers) || []).map(
  pm => store.getters['user/getUser'](pm.userId),
).filter(u => u !== undefined));
const projectUsers = computed(() => {
  if (!mappedUsers.value || !user.value) {
    return [];
  }
  return [...mappedUsers.value].sort((a, b) => {
    if (a.id === user.value.id) return -1;
    if (b.id === user.value.id) return 1;

    if (projectTasksFilter.value && projectTasksFilter.value.createdByIds) {
      const comparison = projectTasksFilter.value.createdByIds.includes(b ? b.id : b)
        - projectTasksFilter.value.createdByIds.includes(a ? a.id : a);
      return comparison === 0 ? (+!a || +!b || +(a.username > b.username) - 1) : comparison;
    }
    return (+!a || +!b || +(a.username > b.username) - 1);
  });
});
const projectUsersSliced = computed(() => projectUsers.value.slice(0, moreCreators.value ? projectUsers.value.length : MORE_THRESHOLD));
const projectUsersAndUnassigned = computed(() => {
  if (!user.value) {
    return [];
  }
  return [...mappedUsers.value, null].sort((a, b) => {
    if (a && a.id === user.value.id) return -1;
    if (b && b.id === user.value.id) return 1;

    if (projectTasksFilter.value && projectTasksFilter.value.assigneeIds) {
      const comparison = projectTasksFilter.value.assigneeIds.includes(b ? b.id : b)
        - projectTasksFilter.value.assigneeIds.includes(a ? a.id : a);
      return comparison === 0 ? (+!a || +!b || +(a.username > b.username) - 1) : comparison;
    }
    return (+!a || +!b || +(a.username > b.username) - 1);
  });
});
const projectUsersAndUnassignedSliced = computed(() => projectUsersAndUnassigned.value
  .slice(0, moreAssignees.value ? projectUsersAndUnassigned.value.length : MORE_THRESHOLD));
const projectUsersAndUnassignedSlicedAssignee = computed(() => projectUsersAndUnassigned.value
  .slice(0, moreAssigneeFilter.value ? projectUsersAndUnassigned.value.length : MORE_THRESHOLD));
const projectTags = computed(() => [...(project.value?.labels || []).filter(l => !l.archived), null].sort(
  (a, b) => +!a || +!b || +(dayjs(a.createdAt).isBefore(dayjs(b.createdAt))) - 1,
));
const projectTagsSliced = computed(() => projectTags.value.slice(0, moreTags.value ? projectTags.value.length : MORE_THRESHOLD));

// dates are taken on click: the panel can stay open past midnight
const presetDatePicker = [
  {
    name: 'reports.last_month',
    id: 'last_month',
    dates: () => ({
      start: dayjs().subtract(1, 'months').startOf('month'),
      end: dayjs().subtract(1, 'months').endOf('month'),
    }),
  },
  {
    name: 'reports.this_month',
    id: 'this_month',
    dates: () => ({ start: dayjs().startOf('month'), end: dayjs() }),
  },
  {
    name: 'reports.this_year',
    id: 'this_year',
    dates: () => ({ start: dayjs().startOf('year'), end: dayjs() }),
  },
  {
    name: 'reports.all_time',
    id: 'all_time',
  },
];

const triggerCheck = (field, id) => {
  store.dispatch('project/actionSwitchFilterByFieldAndId', { field, id });
};
const clearAll = () => {
  store.dispatch('project/actionFilterTasks', { projectId: project.value.id });
  emit('close');
};
// whole days in local time, in order: the API keeps tasks with start <= createdAt <= end, while a
// clicked day keeps the current time of day and a typed date is the start of its day
const setDateInput = (value, id = null) => {
  const [start, end] = [dayjs(value.start), dayjs(value.end)].sort((a, b) => a - b);
  activePreset.value = id;
  triggerCheck('createdAtRange', {
    start: start.startOf('day').toISOString(),
    end: end.endOf('day').toISOString(),
  });
};
const onIsoChange = (edge, value) => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return;
  }
  const typed = parsed.toISOString();
  // with no range yet, one typed date is a one-day range
  const range = createdAtRange.value || { start: typed, end: typed };
  setDateInput({ ...range, [edge]: typed });
};
const clearDates = () => {
  activePreset.value = null;
  triggerCheck('createdAtRange', null);
};
const applyPreset = (item) => {
  if (!item.dates) {
    clearDates();
    return;
  }
  setDateInput(item.dates(), item.id);
};
const checkState = (field, id) => {
  if (!projectTasksFilter.value[field]) {
    return false;
  }
  return projectTasksFilter.value[field].includes(id);
};
</script>

